// Client-facing budget summary and transaction view.
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { projectApi } from "../../backend/api/projects";
import { useProjectBudget } from "../../backend/queries/budgets";
import { ErrorState, LoadingState } from "../../components/patterns";
import { useCurrentUser } from "../../backend/queries/auth";
import {
  Calculator,
  CircleDollarSign,
  Download,
  ReceiptText,
  WalletCards,
} from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

const toMoney = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "");
// Replace unsupported Unicode punctuation for jsPDF's core fonts
const sanitize = (s) =>
  String(s ?? "")
    .replace(/[–—]/g, "-")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'");

const ClientBudgetUI = () => {
  const location = useLocation();

  // 👀 agent-peek flags (navigate('/budgeting', { state: { fromAgent: true, projectId } }))
  const isAgentPeek = location.state?.fromAgent === true;
  const forcedProjectId = location.state?.projectId
    ? Number(location.state.projectId)
    : null;

  const [userId, setUserId] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // firstname/lastname
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);

  const [budgetDetails, setBudgetDetails] = useState({
    total_budget: 0,
    amount_paid: 0,
    amount_outstanding: 0,
    remaining_budget: 0,
  });

  const [outstandingItems, setOutstandingItems] = useState([]);
  const [paidItems, setPaidItems] = useState([]);
  const { data: currentUser } = useCurrentUser();

  // ---------- Auth -> user id + profile (skip for agent-peek) ----------
  useEffect(() => {
    if (isAgentPeek) return; // agent-peek ignores logged-in client's id

    const user = currentUser?.profile;
    if (user?.id) {
      setUserId(user.id);
      setUserProfile({
        firstname: user.firstname || "",
        lastname: user.lastname || "",
      });
    }
  }, [currentUser, isAgentPeek]);

  // ---------- Map to projects ----------
  useEffect(() => {
    const load = async () => {
      // Agent-peek: fetch a single project by id (and grab client name from join)
      if (isAgentPeek && forcedProjectId) {
        const { data, error } = await projectApi.get(forcedProjectId);

        if (error) {
          console.error("Error fetching forced project:", error);
          setProjects([]);
          setSelectedProjectId(null);
          return;
        }

        setProjects([data]);
        setSelectedProjectId(Number(data.id));
        if (data?.customer) {
          setUserProfile({
            firstname: data.customer.firstname || "",
            lastname: data.customer.lastname || "",
          });
        }
        return;
      }

      // Normal client flow
      if (!userId) return;
      const { data, error } = await projectApi.list({ customer_id: userId });

      if (error) {
        console.error("Error fetching projects:", error);
        return;
      }

      setProjects(data || []);
      const preselect =
        forcedProjectId != null &&
        (data || []).some((p) => Number(p.id) === forcedProjectId)
          ? forcedProjectId
          : data?.length
            ? Number(data[0].id)
            : null;
      setSelectedProjectId(preselect);
    };

    load();
  }, [isAgentPeek, forcedProjectId, userId]);

  const budgetQuery = useProjectBudget(selectedProjectId);
  const syncedDates = (budgetQuery.data?.invoices || []).map((invoice) => invoice.synced_at).filter(Boolean);
  const oldestSync = syncedDates.length ? syncedDates.sort()[0] : null;

  useEffect(() => {
    const data = budgetQuery.data;
    setOutstandingItems([]);
    setPaidItems([]);
    setBudgetDetails({ total_budget: 0, amount_paid: 0, amount_outstanding: 0, remaining_budget: 0 });
    if (!data) return;
    // Display Xero's invoice amounts; never infer payment from legacy cost rows.
    const rows = data.invoices.map((invoice) => ({
      id: invoice.id,
      item: invoice.reference || "Relocation services",
      invoice: invoice.xero_invoice_number || "Invoice",
      due: invoice.due_date || "",
      date: invoice.synced_at || "",
      gst_inclusive: false,
      paid: Number(invoice.amount_paid || 0),
      owing: Number(invoice.amount_due || 0),
    }));
    setOutstandingItems(rows.filter((row) => row.owing > 0).map((row) => ({ ...row, cost: row.owing, status: row.paid > 0 ? "Part paid" : "Outstanding" })));
    setPaidItems(rows.filter((row) => row.paid > 0).map((row) => ({ ...row, cost: row.paid, status: row.owing > 0 ? "Part paid" : "Paid" })));
    setBudgetDetails({
      total_budget: Number(data.summary.total_budget),
      amount_paid: Number(data.summary.amount_paid),
      amount_outstanding: Number(data.summary.amount_pending),
      remaining_budget: Number(data.summary.remaining_budget),
    });
  }, [budgetQuery.data]);

  // ---------- PDF export ----------
  const handleDownloadPDF = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const margin = 40;
    let y = margin;

    const proj = projects.find(
      (p) => Number(p.id) === Number(selectedProjectId),
    );
    const projectLine = proj
      ? `Project: ${sanitize(proj.project_type)} - ${sanitize(proj.relocating_from)} -> ${sanitize(proj.relocating_to)}`
      : "";

    const clientName = userProfile
      ? sanitize(
          `${userProfile.firstname || ""} ${userProfile.lastname || ""}`.trim(),
        )
      : "";

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("Relocation Budget Statement", margin, y);
    y += 22;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, y);
    y += 16;
    if (clientName) {
      doc.text(`Client name: ${clientName}`, margin, y);
      y += 16;
    }
    if (projectLine) {
      doc.text(sanitize(projectLine), margin, y);
      y += 18;
    }

    autoTable(doc, {
      startY: y,
      head: [
        [
          "Total Budget (AUD)",
          "Amount Paid (AUD)",
          "Outstanding (AUD)",
          "Remaining (AUD)",
        ],
      ],
      body: [
        [
          toMoney(budgetDetails.total_budget),
          toMoney(budgetDetails.amount_paid),
          toMoney(budgetDetails.amount_outstanding),
          toMoney(budgetDetails.remaining_budget),
        ],
      ],
      styles: { font: "helvetica", fontSize: 10, textColor: [0, 0, 0] },
      headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0] },
      margin: { left: margin, right: margin },
      tableWidth: "auto",
    });
    y = doc.lastAutoTable.finalY + 20;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Outstanding Transactions", margin, y);
    y += 10;
    autoTable(doc, {
      startY: y,
      head: [["Item", "Amount (AUD)", "Due Date", "Invoice", "Status"]],
      body: outstandingItems.length
        ? outstandingItems.map((o) => [
            sanitize(o.item || ""),
            toMoney(o.cost) + (o.gst_inclusive ? " (GST incl.)" : ""),
            fmtDate(o.due),
            sanitize(o.invoice || ""),
            o.status,
          ])
        : [["No outstanding transactions", "", "", "", ""]],
      styles: { font: "helvetica", fontSize: 10, textColor: [0, 0, 0] },
      headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0] },
      margin: { left: margin, right: margin },
      tableWidth: "auto",
    });
    y = doc.lastAutoTable.finalY + 20;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.text("Paid Transactions", margin, y);
    y += 10;
    autoTable(doc, {
      startY: y,
      head: [["Item", "Amount (AUD)", "Last synced", "Invoice", "Status"]],
      body: paidItems.length
        ? paidItems.map((p) => [
            sanitize(p.item || ""),
            toMoney(p.cost) + (p.gst_inclusive ? " (GST incl.)" : ""),
            fmtDate(p.date),
            sanitize(p.invoice || ""),
            p.status,
          ])
        : [["No paid transactions", "", "", "", ""]],
      styles: { font: "helvetica", fontSize: 10, textColor: [0, 0, 0] },
      headStyles: { fillColor: [230, 230, 230], textColor: [0, 0, 0] },
      margin: { left: margin, right: margin },
      tableWidth: "auto",
    });

    const fileBase = (() => {
      if (!proj) return "project";
      const base =
        (proj.project_type ? `${proj.project_type} - ` : "") +
        `${proj.relocating_from || ""} -> ${proj.relocating_to || ""}`.trim();
      return String(base || "project")
        .replace(/[^\w-]+/g, "_")
        .toLowerCase();
    })();

    doc.save(`${fileBase}_relocation_budget_statement.pdf`);
  };

  const dataReady = projects.length > 0;

  return (
    <div className="flex flex-col">

      <div className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-2xl font-semibold">Budgeting</h1>
              <p className="text-sm text-muted-foreground">
                Track planned budget, paid items, and outstanding payments.
              </p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              {projects.length > 0 && (
                <Select
                  value={
                    selectedProjectId == null
                      ? undefined
                      : String(selectedProjectId)
                  }
                  onValueChange={(value) =>
                    !isAgentPeek && setSelectedProjectId(Number(value))
                  }
                  disabled={isAgentPeek}
                >
                  <SelectTrigger className="w-full sm:w-[420px]">
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={String(project.id)}>
                        {project.project_type} from {project.relocating_from} to{" "}
                        {project.relocating_to}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button onClick={handleDownloadPDF} disabled={!dataReady || budgetQuery.isPending || budgetQuery.isError}>
                <Download className="h-4 w-4" />
                Export PDF
              </Button>
            </div>
          </div>

          {selectedProjectId != null && budgetQuery.isPending && <LoadingState label="Loading invoices" />}
          {budgetQuery.isError && <ErrorState title="Could not load invoices" onRetry={() => budgetQuery.refetch()} />}
          {!budgetQuery.isPending && !budgetQuery.isError && <>
          <p className="text-sm text-muted-foreground">
            Payments reflect linked Xero invoices. {oldestSync ? `Last synced ${new Date(oldestSync).toLocaleString()}.` : "No synced invoices yet."}
            {" "}Part-paid invoices show the received amount and the remaining balance separately.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <BudgetCard
              icon={WalletCards}
              label="Total budget"
              amount={budgetDetails.total_budget}
            />
            <BudgetCard
              icon={ReceiptText}
              label="Amount paid"
              amount={budgetDetails.amount_paid}
              tone="success"
            />
            <BudgetCard
              icon={CircleDollarSign}
              label="Outstanding"
              amount={budgetDetails.amount_outstanding}
              tone="warning"
            />
            <BudgetCard
              icon={Calculator}
              label="Remaining"
              amount={budgetDetails.remaining_budget}
              tone="info"
            />
          </div>

          <div className="grid gap-5 xl:grid-cols-2">
            <SectionTable
              title="Outstanding payments"
              data={outstandingItems}
              isPaid={false}
            />
            <SectionTable title="Payments received" data={paidItems} isPaid />
          </div>
          </>}
        </div>
      </div>
    </div>
  );
};

const BudgetCard = ({ icon: Icon, label, amount, tone }: any) => (
  <Card>
    <CardHeader className="flex-row items-center justify-between space-y-0 p-4 pb-2">
      <CardTitle className="text-sm font-medium text-muted-foreground">
        {label}
      </CardTitle>
      <Icon
        className={`h-4 w-4 ${
          tone === "success"
            ? "text-tone-success-foreground"
            : tone === "warning"
              ? "text-tone-warning-foreground"
              : tone === "info"
                ? "text-tone-info-foreground"
                : "text-primary"
        }`}
      />
    </CardHeader>
    <CardContent className="p-4 pt-0">
      <div className="text-2xl font-semibold">{toMoney(amount)}</div>
    </CardContent>
  </Card>
);

const SectionTable = ({ title, data, isPaid }) => (
  <Card>
    <CardHeader className="p-4 sm:p-5">
      <CardTitle>{title}</CardTitle>
      <CardDescription>{data?.length || 0} invoices</CardDescription>
    </CardHeader>
    <CardContent className="p-4 pt-0 sm:p-5 sm:pt-0">
      <div className="space-y-3 md:hidden">
        {data && data.length > 0 ? (
          data.map((entry, index) => (
            <div key={index} className="rounded-md border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold">{entry.item}</div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {isPaid ? "Last synced" : "Due"}:{" "}
                    {isPaid ? fmtDate(entry.date) : fmtDate(entry.due)}
                  </div>
                </div>
                <Badge variant={isPaid ? "success" : "warning"}>
                  {entry.status}
                </Badge>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted-foreground">{isPaid ? "Received" : "Amount due"}</div>
                  <div className="font-medium">
                    {toMoney(entry.cost)}
                    {entry.gst_inclusive ? (
                      <span className="ml-1 text-xs font-semibold text-tone-success-foreground">
                        GST incl.
                      </span>
                    ) : null}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Invoice</div>
                  <div className="font-medium">{entry.invoice || "N/A"}</div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
            No items found.
          </div>
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-md border md:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Item</TableHead>
              <TableHead>{isPaid ? "Received" : "Amount due"}</TableHead>
              <TableHead>{isPaid ? "Last synced" : "Due date"}</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data && data.length > 0 ? (
              data.map((entry, i) => (
                <TableRow key={i}>
                  <TableCell className="font-medium">{entry.item}</TableCell>
                  <TableCell>
                    {toMoney(entry.cost)}{" "}
                    {entry.gst_inclusive ? (
                      <span className="text-xs font-semibold text-tone-success-foreground">
                        (GST incl.)
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {isPaid ? fmtDate(entry.date) : fmtDate(entry.due)}
                  </TableCell>
                  <TableCell>{entry.invoice || "N/A"}</TableCell>
                  <TableCell>
                    <Badge variant={isPaid ? "success" : "warning"}>
                      {entry.status}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={5}
                  className="h-24 text-center text-muted-foreground"
                >
                  No items found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </CardContent>
  </Card>
);

export default ClientBudgetUI;
